/**
 * eventForm — the event-page, reminder and certificate fields of a webinar's
 * form: what a new webinar starts with, how a saved one loads, and what is
 * sent back. Kept out of DemoClassesFormPage so that file only wires it in.
 *
 * ⚠️ A webinar saved before these fields existed has NONE of them, and each
 * absence means something specific on the backend (see
 * vc_be/utils/sessionReminders.js): no `reminderOffsets` is the legacy
 * 24h + 1h ladder, no `followUp` is "feedback on, missed-you off", no
 * `certificate` is "off". Loading must show exactly that — never the new-webinar
 * defaults — or merely opening and saving an old webinar would change what its
 * registrants receive.
 */

/** A Date / ISO string as `<input type="datetime-local">` wants it, in the browser's zone. */
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
};

export const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

export const EVENT_TYPE_OPTIONS = [
  { value: "webinar", label: "Webinar" },
  { value: "seminar", label: "Seminar" },
  { value: "workshop", label: "Workshop" },
  { value: "masterclass", label: "Masterclass" },
  { value: "info-session", label: "Info session" },
];

/** Offered reminder offsets, furthest first. The backend sends the same list. */
export const REMINDER_OPTIONS = [
  { minutes: 1440, title: "1 day before" },
  { minutes: 120, title: "2 hours before" },
  { minutes: 60, title: "1 hour before" },
  { minutes: 30, title: "30 minutes before" },
  { minutes: 15, title: "15 minutes before" },
];

const LEGACY_OFFSETS = [1440, 60];

const emptyWebsite = () => ({
  listed: false,
  slug: "",
  summary: "",
  coverImageKey: "",
  highlights: [],
  audience: [],
  agenda: [],
  speakers: [],
  faqs: [],
});

/** What a NEW webinar starts with — the workflow the client asked for, switched on. */
export const newEventFields = () => ({
  eventType: "webinar",
  website: { ...emptyWebsite(), listed: true },
  whatsappGroupUrl: "",
  reminderOffsets: [1440, 120, 30, 15],
  followUp: { feedback: true, missedYou: true },
  certificate: { enabled: true, minAttendancePct: 80, autoSend: true, signatoryName: "", signatoryTitle: "" },
});

/** A saved session's fields, with absences read the way the backend reads them. */
export const eventFieldsFromSession = (s = {}) => ({
  eventType: s.eventType || "webinar",
  website: { ...emptyWebsite(), ...(s.website || {}) },
  whatsappGroupUrl: s.whatsappGroupUrl || "",
  reminderOffsets: Array.isArray(s.reminderOffsets) ? s.reminderOffsets : LEGACY_OFFSETS,
  followUp: {
    feedback: s.followUp?.feedback !== false,
    missedYou: s.followUp?.missedYou === true,
  },
  certificate: {
    enabled: !!s.certificate?.enabled,
    minAttendancePct: s.certificate?.minAttendancePct ?? 80,
    autoSend: s.certificate?.autoSend !== false,
    signatoryName: s.certificate?.signatoryName || "",
    signatoryTitle: s.certificate?.signatoryTitle || "",
  },
});

const trimRows = (rows, keys) => (rows || [])
  .map((r) => (typeof r === "string" ? r.trim() : r))
  .filter((r) => (typeof r === "string" ? r : keys.some((k) => String(r?.[k] || "").trim())));

/** The event fields as the API takes them — empty rows dropped. */
export const eventPayload = (form) => ({
  eventType: form.eventType,
  website: {
    ...form.website,
    highlights: trimRows(form.website.highlights, []),
    audience: trimRows(form.website.audience, []),
    agenda: trimRows(form.website.agenda, ["title"]),
    speakers: trimRows(form.website.speakers, ["name"]),
    faqs: trimRows(form.website.faqs, ["question", "answer"]),
  },
  whatsappGroupUrl: (form.whatsappGroupUrl || "").trim(),
  reminderOffsets: [...new Set(form.reminderOffsets)].sort((a, b) => b - a),
  followUp: form.followUp,
  certificate: {
    ...form.certificate,
    minAttendancePct: Number(form.certificate.minAttendancePct) || 80,
  },
});

/** The keys an ENDED session may still change — the backend refuses the rest. */
export const POST_END_FIELDS = ["certificate", "followUp", "recording", "website", "whatsappGroupUrl"];

/**
 * Preview URLs for the images a saved session already has, keyed by storage
 * key: the backend resolves them on read (`eventImages`), and an upload adds
 * its own as it happens.
 */
export const previewsFromSession = (session, eventImages) => {
  const out = {};
  const w = session?.website || {};
  if (w.coverImageKey && eventImages?.coverImageUrl) out[w.coverImageKey] = eventImages.coverImageUrl;
  (w.speakers || []).forEach((sp, i) => {
    const resolved = eventImages?.speakers?.[i]?.photoUrl;
    if (sp.photoKey && resolved) out[sp.photoKey] = resolved;
  });
  return out;
};

/** Client-side checks the backend also makes, said before the round trip. */
export const validateEventFields = (form) => {
  const errors = {};
  const url = (form.whatsappGroupUrl || "").trim();
  if (url && !/^https:\/\/(chat\.whatsapp\.com|(www\.)?whatsapp\.com|wa\.me)\//i.test(url)) {
    errors.whatsappGroupUrl = "Paste the invite link from WhatsApp — it starts with https://chat.whatsapp.com/";
  }
  const pct = Number(form.certificate?.minAttendancePct);
  if (form.certificate?.enabled && (!Number.isFinite(pct) || pct < 1 || pct > 100)) {
    errors.minAttendancePct = "Enter a percentage between 1 and 100.";
  }
  if (form.website?.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.website.slug)) {
    errors.slug = "Use lowercase letters, numbers and hyphens only.";
  }
  return errors;
};

/** `Careers in AI!` → `careers-in-ai` — the same rule the backend applies. */
export const slugify = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .replace(/&/g, " and ")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 80);
